import * as pdfjsLib from 'pdfjs-dist'
import { PDFDocument } from 'pdf-lib'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).href

export class PdfCompressApp {
  constructor() {
    this.container = null;
    this.selectedFile = null;
    this.pdfDocument = null;
    this.settings = this.getDefaultSettings();
    this.originalSize = 0;
  }

  getDefaultSettings() {
    return {
      // Main settings
      targetSizeMode: 'quality', // 'quality', 'size', 'custom'
      targetSizeMB: 5,
      quality: 0.7, // Image quality for compression (0.1 - 1.0)

      // Advanced settings
      scale: 1.5, // DPI reduction (1=72dpi, 1.5=108dpi, 2=144dpi)
      removeMetadata: false,
      grayscale: false,
      customQuality: false,
    };
  }

  init(container) {
    this.container = container;
    this.render();
    this.setupEventListeners();
  }

  render() {
    this.container.innerHTML = `
      <div class="container">
        <div class="pdfcompress-app">
          <!-- File Upload Section -->
          <div class="upload-section">
            <div class="upload-area" id="upload-area">
              <div class="upload-icon">📄</div>
              <h3>Select PDF File to Compress</h3>
              <p>Upload a PDF file to reduce its size</p>
              <button class="upload-button" id="file-select-btn">
                <span>📎 Choose PDF File</span>
              </button>
              <input type="file" id="file-input" accept=".pdf,application/pdf" style="display: none;">
              <div class="upload-hint">Or drag and drop a PDF file here</div>
            </div>
          </div>

          <!-- PDF Info Section -->
          <div class="pdf-info-section" id="pdf-info-section" style="display: none;">
            <div class="section-header">
              <h3>📄 PDF Information</h3>
            </div>
            <div class="pdf-info-content">
              <div class="info-item">
                <span class="info-label">File:</span>
                <span class="info-value" id="pdf-filename"></span>
              </div>
              <div class="info-item">
                <span class="info-label">Pages:</span>
                <span class="info-value" id="pdf-pages"></span>
              </div>
              <div class="info-item">
                <span class="info-label">Original Size:</span>
                <span class="info-value" id="pdf-size"></span>
              </div>
            </div>
            <button class="action-button" id="change-file">📎 Change File</button>
          </div>

          <!-- Settings Section -->
          <div class="settings-section" id="settings-section" style="display: none;">
            <h3>Compression Settings</h3>

            <!-- Main Settings -->
            <div class="settings-group main-settings">
              <div class="setting-row">
                <label for="compression-preset">🎯 Compression Preset:</label>
                <select id="compression-preset" class="setting-input">
                  <option value="high">High Quality (Minimal compression)</option>
                  <option value="medium" selected>Medium Quality (Balanced)</option>
                  <option value="low">Low Quality (Maximum compression)</option>
                  <option value="custom">Custom Settings</option>
                </select>
              </div>

              <div class="setting-row" id="target-size-row" style="display: none;">
                <label for="target-size">🎯 Target Size (MB):</label>
                <input type="number" id="target-size" class="setting-input"
                       min="0.1" step="0.1" value="5"
                       placeholder="e.g., 5">
                <div class="setting-hint">Approximate target file size</div>
              </div>

              <div class="setting-row" id="quality-row" style="display: none;">
                <label for="quality">✨ Image Quality:</label>
                <div class="quality-control">
                  <input type="range" id="quality" class="quality-slider"
                         min="0.1" max="1.0" step="0.05" value="0.7">
                  <span class="quality-value" id="quality-value">70%</span>
                </div>
                <div class="setting-hint">Lower quality = smaller file size</div>
              </div>

              <div class="setting-row" id="scale-row" style="display: none;">
                <label for="scale">📏 Image Resolution:</label>
                <select id="scale" class="setting-input">
                  <option value="1">72 DPI (Maximum compression)</option>
                  <option value="1.5" selected>108 DPI (Balanced)</option>
                  <option value="2">144 DPI (Higher quality)</option>
                  <option value="2.5">180 DPI (Minimal compression)</option>
                </select>
              </div>
            </div>

            <!-- Compression Info Section -->
            <div class="compression-info-section" id="compression-info-section" style="display: none;">
              <div class="compression-info-box">
                <h4>📊 Compression Settings</h4>
                <div class="info-grid">
                  <div class="info-item-compress">
                    <span class="info-label-compress">Original Size:</span>
                    <span class="info-value-compress" id="original-size-display">-</span>
                  </div>
                  <div class="info-item-compress">
                    <span class="info-label-compress">Quality Level:</span>
                    <span class="info-value-compress" id="quality-level-display">Medium</span>
                  </div>
                  <div class="info-item-compress">
                    <span class="info-label-compress">Resolution:</span>
                    <span class="info-value-compress" id="resolution-display">108 DPI</span>
                  </div>
                </div>
                <div class="compression-note">
                  💡 Actual compressed size will be determined after processing
                </div>
              </div>
            </div>

            <!-- Advanced Settings Toggle -->
            <button class="advanced-toggle" id="advanced-toggle">
              <span>⚙️ Advanced Settings</span>
              <span class="toggle-icon">▼</span>
            </button>

            <!-- Advanced Settings (Hidden by default) -->
            <div class="settings-group advanced-settings" id="advanced-settings" style="display: none;">
              <div class="setting-row">
                <label for="remove-metadata">🗑️ Remove Metadata:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="remove-metadata" class="setting-checkbox">
                  <span class="checkbox-label">Remove PDF metadata to reduce size</span>
                </div>
              </div>

              <div class="setting-row">
                <label for="grayscale">⚫ Convert to Grayscale:</label>
                <div class="checkbox-group">
                  <input type="checkbox" id="grayscale" class="setting-checkbox">
                  <span class="checkbox-label">Convert to grayscale for smaller size</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Compress Section -->
          <div class="compress-section" id="compress-section" style="display: none;">
            <button class="convert-button" id="compress-button">
              <span class="convert-icon">🗜️</span>
              <span class="convert-text">Compress PDF</span>
            </button>

            <div class="progress-section" id="progress-section" style="display: none;">
              <div class="progress-bar">
                <div class="progress-fill" id="progress-fill"></div>
              </div>
              <div class="progress-text" id="progress-text">Compressing...</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  setupEventListeners() {
    const fileInput = document.getElementById('file-input');
    const fileSelectBtn = document.getElementById('file-select-btn');
    const uploadArea = document.getElementById('upload-area');
    const changeFileBtn = document.getElementById('change-file');
    const compressBtn = document.getElementById('compress-button');
    const advancedToggle = document.getElementById('advanced-toggle');

    // File selection
    fileSelectBtn.addEventListener('click', () => fileInput.click());
    changeFileBtn.addEventListener('click', () => fileInput.click());
    fileInput.addEventListener('change', (e) => this.handleFileSelection(e.target.files[0]));

    // Drag and drop
    uploadArea.addEventListener('dragover', this.handleDragOver.bind(this));
    uploadArea.addEventListener('drop', this.handleFileDrop.bind(this));
    uploadArea.addEventListener('dragleave', this.handleDragLeave.bind(this));

    // Compress button
    compressBtn.addEventListener('click', () => this.compressPDF());

    // Advanced settings toggle
    advancedToggle.addEventListener('click', () => this.toggleAdvancedSettings());

    // Settings change listeners
    this.setupSettingsListeners();
  }

  setupSettingsListeners() {
    // Compression preset
    document.getElementById('compression-preset').addEventListener('change', (e) => {
      const preset = e.target.value;
      this.applyPreset(preset);
    });

    // Target size
    document.getElementById('target-size').addEventListener('input', (e) => {
      this.settings.targetSizeMB = parseFloat(e.target.value);
    });

    // Quality
    document.getElementById('quality').addEventListener('input', (e) => {
      this.settings.quality = parseFloat(e.target.value);
      document.getElementById('quality-value').textContent = Math.round(e.target.value * 100) + '%';
      this.updateCompressionInfo();
    });

    // Scale
    document.getElementById('scale').addEventListener('change', (e) => {
      this.settings.scale = parseFloat(e.target.value);
      this.updateCompressionInfo();
    });

    // Remove metadata
    document.getElementById('remove-metadata').addEventListener('change', (e) => {
      this.settings.removeMetadata = e.target.checked;
    });

    // Grayscale
    document.getElementById('grayscale').addEventListener('change', (e) => {
      this.settings.grayscale = e.target.checked;
      this.updateCompressionInfo();
    });
  }

  applyPreset(preset) {
    const presets = {
      high: { quality: 0.85, scale: 2.5, label: 'High Quality' },
      medium: { quality: 0.7, scale: 1.5, label: 'Medium Quality' },
      low: { quality: 0.5, scale: 1, label: 'Low Quality' },
      custom: { quality: 0.7, scale: 1.5, label: 'Custom' }
    };

    const config = presets[preset];
    this.settings.quality = config.quality;
    this.settings.scale = config.scale;

    // Update UI
    document.getElementById('quality').value = config.quality;
    document.getElementById('quality-value').textContent = Math.round(config.quality * 100) + '%';
    document.getElementById('scale').value = config.scale;

    // Show/hide custom settings
    const customRows = ['quality-row', 'scale-row', 'target-size-row'];
    customRows.forEach(rowId => {
      document.getElementById(rowId).style.display = preset === 'custom' ? 'grid' : 'none';
    });

    // Update info display
    this.updateCompressionInfo();
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    document.getElementById('upload-area').classList.add('drag-over');
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    document.getElementById('upload-area').classList.remove('drag-over');
  }

  handleFileDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    document.getElementById('upload-area').classList.remove('drag-over');

    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].type === 'application/pdf') {
      this.handleFileSelection(files[0]);
    }
  }

  async handleFileSelection(file) {
    if (!file || file.type !== 'application/pdf') {
      this.showError('Please select a valid PDF file');
      return;
    }

    this.selectedFile = file;
    this.originalSize = file.size;

    try {
      // Load PDF with PDF.js for analysis
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      this.pdfDocument = await loadingTask.promise;

      // Show PDF info
      this.displayPDFInfo();
      this.updateCompressionInfo();

    } catch (error) {
      console.error('Error loading PDF:', error);
      this.showError('Failed to load PDF file');
    }
  }

  displayPDFInfo() {
    // Update file info
    document.getElementById('pdf-filename').textContent = this.selectedFile.name;
    document.getElementById('pdf-pages').textContent = this.pdfDocument.numPages;
    document.getElementById('pdf-size').textContent = this.formatFileSize(this.originalSize);
    document.getElementById('original-size-display').textContent = this.formatFileSize(this.originalSize);

    // Show sections
    document.getElementById('pdf-info-section').style.display = 'block';
    document.getElementById('settings-section').style.display = 'block';
    document.getElementById('compress-section').style.display = 'block';
    document.getElementById('compression-info-section').style.display = 'block';
  }

  updateCompressionInfo() {
    if (!this.pdfDocument) return;

    // Update original size display
    document.getElementById('original-size-display').textContent = this.formatFileSize(this.originalSize);

    // Update quality level display
    const preset = document.getElementById('compression-preset').value;
    const presetLabels = {
      high: 'High Quality',
      medium: 'Medium Quality',
      low: 'Low Quality (Max compression)',
      custom: `Custom (${Math.round(this.settings.quality * 100)}%)`
    };
    document.getElementById('quality-level-display').textContent = presetLabels[preset] || 'Medium Quality';

    // Update resolution display
    const dpiValue = Math.round(this.settings.scale * 72);
    document.getElementById('resolution-display').textContent = `${dpiValue} DPI`;
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }

  toggleAdvancedSettings() {
    const advancedSettings = document.getElementById('advanced-settings');
    const toggleIcon = document.querySelector('.toggle-icon');

    if (advancedSettings.style.display === 'none') {
      advancedSettings.style.display = 'block';
      toggleIcon.textContent = '▲';
    } else {
      advancedSettings.style.display = 'none';
      toggleIcon.textContent = '▼';
    }
  }

  async compressPDF() {
    if (!this.pdfDocument || !this.selectedFile) {
      this.showError('Please select a PDF file first');
      return;
    }

    try {
      this.showProgress('Initializing compression...');

      const totalPages = this.pdfDocument.numPages;
      const compressedImages = [];

      // Convert each page to image and compress
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const progress = (pageNum - 1) / totalPages;
        this.updateProgress(
          `Compressing page ${pageNum} of ${totalPages}...`,
          progress
        );

        const imageData = await this.compressPage(pageNum);
        compressedImages.push(imageData);
      }

      this.updateProgress('Rebuilding PDF...', 0.9);

      // Create new PDF with compressed images
      const compressedPDF = await this.rebuildPDF(compressedImages);

      this.updateProgress('Preparing download...', 0.95);

      // Download the compressed PDF
      const originalName = this.selectedFile.name.replace('.pdf', '');
      const filename = `${originalName}-compressed.pdf`;
      this.downloadPDF(compressedPDF, filename);

      this.updateProgress('Complete!', 1.0);
      this.hideProgress();

      const compressedSize = compressedPDF.length;
      const reduction = ((this.originalSize - compressedSize) / this.originalSize * 100).toFixed(1);
      this.showSuccess(
        `PDF compressed successfully!<br>` +
        `Original: ${this.formatFileSize(this.originalSize)}<br>` +
        `Compressed: ${this.formatFileSize(compressedSize)}<br>` +
        `Reduction: ${reduction}%`
      );

    } catch (error) {
      console.error('Error compressing PDF:', error);
      this.hideProgress();
      this.showError(`Compression failed: ${error.message}`);
    }
  }

  async compressPage(pageNumber) {
    // Get the page
    const page = await this.pdfDocument.getPage(pageNumber);
    const viewport = page.getViewport({ scale: this.settings.scale });

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    const context = canvas.getContext('2d');

    // Set white background
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    // Render page to canvas
    await page.render({
      canvasContext: context,
      viewport: viewport
    }).promise;

    // Apply grayscale if needed
    if (this.settings.grayscale) {
      this.applyGrayscale(context, canvas.width, canvas.height);
    }

    // Convert canvas to JPEG blob (always use JPEG for compression)
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', this.settings.quality);
    });

    return {
      blob: blob,
      width: viewport.width,
      height: viewport.height
    };
  }

  applyGrayscale(context, width, height) {
    const imageData = context.getImageData(0, 0, width, height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = gray;
      data[i + 1] = gray;
      data[i + 2] = gray;
    }

    context.putImageData(imageData, 0, 0);
  }

  async rebuildPDF(compressedImages) {
    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();

    // Add each compressed image as a page
    for (const imageData of compressedImages) {
      const arrayBuffer = await imageData.blob.arrayBuffer();
      const image = await pdfDoc.embedJpg(arrayBuffer);

      const page = pdfDoc.addPage([imageData.width, imageData.height]);
      page.drawImage(image, {
        x: 0,
        y: 0,
        width: imageData.width,
        height: imageData.height,
      });
    }

    // Set metadata if not removing
    if (!this.settings.removeMetadata) {
      pdfDoc.setTitle(this.selectedFile.name.replace('.pdf', ''));
      pdfDoc.setProducer('PDF Tools Online - Compressor');
      pdfDoc.setCreationDate(new Date());
    }

    // Save the PDF
    const pdfBytes = await pdfDoc.save();
    return pdfBytes;
  }

  downloadPDF(pdfBytes, filename) {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  showProgress(message) {
    document.getElementById('progress-section').style.display = 'block';
    document.getElementById('progress-text').textContent = message;
    document.getElementById('progress-fill').style.width = '0%';
  }

  updateProgress(message, progress) {
    document.getElementById('progress-text').textContent = message;
    document.getElementById('progress-fill').style.width = (progress * 100) + '%';
  }

  hideProgress() {
    setTimeout(() => {
      document.getElementById('progress-section').style.display = 'none';
    }, 1000);
  }

  showError(message) {
    alert('Error: ' + message);
  }

  showSuccess(message) {
    // Create a simple success message
    const messageDiv = document.createElement('div');
    messageDiv.className = 'success-message';
    messageDiv.innerHTML = message;
    messageDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #4CAF50;
      color: white;
      padding: 20px 40px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      z-index: 10000;
      text-align: center;
      line-height: 1.6;
    `;
    document.body.appendChild(messageDiv);

    setTimeout(() => {
      messageDiv.remove();
    }, 4000);
  }

  destroy() {
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
      this.pdfDocument = null;
    }
    this.selectedFile = null;
    this.container = null;
  }
}
